import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../lib/i18n";
import { Camera, Upload, Loader2, Check } from "lucide-react";
import { uniqueRanches } from "../../lib/ranchUtils";

const EMPTY = {
  employee_id: "", receipt_date: "", vendor: "", amount: "", card_last4: "",
  ranch_id: "", job_category: "", job_code_id: "", block: "", equipment_ids: [],
  expense_category: "", needs_reimbursement: false, reimbursement_method: "payroll",
  reimbursement_notes: "", notes: "", receipt_storage_path: null, receipt_content_type: null,
};

export default function ExpenseSubmit({ ranches, employees, jobcodes, equipment, categories, onSubmitted }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const isAdmin = user?.role === "admin";
  const selfId = user?.employee_id || user?.id;
  const [f, setF] = useState(EMPTY);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const jobCats = useMemo(() => [...new Set(jobcodes.map((j) => j.category).filter(Boolean))].sort(), [jobcodes]);
  const jobsForCat = useMemo(() => jobcodes.filter((j) => j.category === f.job_category), [jobcodes, f.job_category]);

  // auto-suggest ranch + job category from active timecard
  const suggestFor = isAdmin ? f.employee_id : selfId;
  useEffect(() => {
    if (!suggestFor) return;
    (async () => {
      try {
        const { data } = await api.get(`/expenses/suggest?employee_id=${suggestFor}`);
        setF((p) => ({ ...p, ranch_id: p.ranch_id || data.ranch_id || "", job_category: p.job_category || data.job_category || "" }));
      } catch { /* ignore */ }
    })();
  }, [suggestFor]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/expenses/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = data.extracted || {};
      setF((p) => ({
        ...p,
        receipt_storage_path: data.receipt_storage_path, receipt_content_type: data.receipt_content_type,
        receipt_date: ex.receipt_date || p.receipt_date, vendor: ex.vendor || p.vendor,
        amount: ex.amount != null ? String(ex.amount) : p.amount, card_last4: ex.card_last4 || p.card_last4,
      }));
    } catch (err) {
      setError(t("exps.err_read"));
    } finally {
      setScanning(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.ranch_id || !f.job_category || !f.expense_category || (isAdmin && !f.employee_id)) {
      setError(t("exps.err_req"));
      return;
    }
    setSaving(true);
    try {
      const emp = employees.find((x) => x.id === f.employee_id);
      await api.post("/expenses", {
        ...f,
        employee_id: isAdmin ? f.employee_id : selfId,
        employee_name: isAdmin ? emp?.name : user.name,
        amount: parseFloat(f.amount) || 0,
        job_code_id: f.job_code_id || null,
      });
      setDone(true);
      setTimeout(() => { setDone(false); setF(EMPTY); onSubmitted?.(); }, 1400);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleEquip = (id) => set("equipment_ids", f.equipment_ids.includes(id) ? f.equipment_ids.filter((x) => x !== id) : [...f.equipment_ids, id]);

  const L = "text-[11px] font-bold uppercase tracking-wide text-stone-500 block mb-1";
  const I = "w-full h-12 px-3 border border-stone-200 text-[15px]";

  if (done) return (
    <div className="py-20 text-center" data-testid="expense-submitted">
      <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#7C9673] text-white mb-4"><Check size={32} /></div>
      <div className="text-xl font-bold">{t("exps.submitted")}</div>
    </div>
  );

  return (
    <form onSubmit={submit} className="max-w-xl mx-auto space-y-5" data-testid="expense-submit-form">
      {/* Receipt capture */}
      <div className="bg-white border border-stone-200 p-4">
        <div className="grid grid-cols-2 gap-3">
          <label className="h-28 border-2 border-dashed border-stone-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#1F3D34]" data-testid="receipt-camera">
            <Camera size={26} /><span className="text-xs font-bold uppercase mt-1">{t("exps.take_photo")}</span>
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} />
          </label>
          <label className="h-28 border-2 border-dashed border-stone-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#1F3D34]" data-testid="receipt-upload">
            <Upload size={26} /><span className="text-xs font-bold uppercase mt-1">{t("exps.upload")}</span>
            <input type="file" accept="image/*" className="hidden" onChange={onFile} />
          </label>
        </div>
        {scanning && <div className="flex items-center gap-2 text-sm text-stone-600 mt-3"><Loader2 className="animate-spin" size={16} /> {t("exps.scanning")}</div>}
        {f.receipt_storage_path && !scanning && <div className="text-xs text-[#1F3D34] font-bold uppercase mt-3 flex items-center gap-1"><Check size={14} /> {t("exps.attached")}</div>}
      </div>

      {/* Extracted (editable) */}
      <div className="bg-white border border-stone-200 p-4 grid grid-cols-2 gap-3">
        <div><label className={L}>{t("exps.date")}</label><input data-testid="exp-date" type="date" value={f.receipt_date} onChange={(e) => set("receipt_date", e.target.value)} className={I} /></div>
        <div><label className={L}>{t("exps.amount")}</label><input data-testid="exp-amount" type="number" step="0.01" value={f.amount} onChange={(e) => set("amount", e.target.value)} className={I} /></div>
        <div><label className={L}>{t("exps.vendor")}</label><input data-testid="exp-vendor" value={f.vendor} onChange={(e) => set("vendor", e.target.value)} className={I} /></div>
        <div><label className={L}>{t("exps.card")}</label><input data-testid="exp-card" value={f.card_last4 || ""} maxLength={4} onChange={(e) => set("card_last4", e.target.value)} className={I} /></div>
      </div>

      {/* Coding */}
      <div className="bg-white border border-stone-200 p-4 space-y-3">
        {isAdmin && (
          <div><label className={L}>{t("exps.employee")}</label>
            <select data-testid="exp-employee" value={f.employee_id} onChange={(e) => set("employee_id", e.target.value)} className={`${I} bg-white`}>
              <option value="">{t("exps.sel_employee")}</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><label className={L}>{t("exps.ranch")}</label>
            <select data-testid="exp-ranch" value={f.ranch_id} onChange={(e) => set("ranch_id", e.target.value)} className={`${I} bg-white`}>
              <option value="">{t("exps.select")}</option>
              {uniqueRanches(ranches).map((r) => <option key={r.id} value={r.id}>{r.ranch_number} · {r.name}</option>)}
            </select>
          </div>
          <div><label className={L}>{t("exps.expcat")}</label>
            <select data-testid="exp-category" value={f.expense_category} onChange={(e) => set("expense_category", e.target.value)} className={`${I} bg-white`}>
              <option value="">{t("exps.select")}</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div><label className={L}>{t("exps.jobcat")}</label>
            <select data-testid="exp-jobcat" value={f.job_category} onChange={(e) => { set("job_category", e.target.value); set("job_code_id", ""); }} className={`${I} bg-white`}>
              <option value="">{t("exps.select")}</option>
              {jobCats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div><label className={L}>{t("exps.jobcode")}</label>
            <select data-testid="exp-jobcode" value={f.job_code_id} onChange={(e) => set("job_code_id", e.target.value)} className={`${I} bg-white`}>
              <option value="">{t("exps.optional")}</option>
              {jobsForCat.map((j) => <option key={j.id} value={j.id}>{j.job_code || j.code} · {j.job || j.description}</option>)}
            </select>
          </div>
          <div><label className={L}>{t("exps.block")}</label><input data-testid="exp-block" value={f.block} onChange={(e) => set("block", e.target.value)} className={I} /></div>
        </div>
        {equipment.length > 0 && (
          <details><summary className="text-xs font-bold uppercase text-stone-500 cursor-pointer">{t("exps.equipment")}</summary>
            <div className="flex flex-wrap gap-2 mt-2">
              {equipment.map((eq) => (
                <button type="button" key={eq.id} onClick={() => toggleEquip(eq.id)} className={`px-3 h-9 border text-xs font-bold ${f.equipment_ids.includes(eq.id) ? "bg-[#1F3D34] text-white border-[#1F3D34]" : "border-stone-300"}`}>{eq.name}</button>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Reimbursement */}
      <div className="bg-white border border-stone-200 p-4 space-y-3">
        <label className="flex items-center gap-3 cursor-pointer">
          <input data-testid="exp-reimburse" type="checkbox" checked={f.needs_reimbursement} onChange={(e) => set("needs_reimbursement", e.target.checked)} className="h-5 w-5" />
          <span className="text-sm font-medium">{t("exps.reimb")}</span>
        </label>
        {f.needs_reimbursement && (
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div><label className={L}>{t("exps.reimb_method")}</label>
              <select data-testid="exp-reimburse-method" value={f.reimbursement_method} onChange={(e) => set("reimbursement_method", e.target.value)} className={`${I} bg-white`}>
                <option value="payroll">{t("exps.payroll")}</option><option value="check">{t("exps.check")}</option>
              </select>
            </div>
            <div><label className={L}>{t("exps.notes")}</label><input data-testid="exp-reimburse-notes" value={f.reimbursement_notes} onChange={(e) => set("reimbursement_notes", e.target.value)} className={I} /></div>
          </div>
        )}
      </div>

      <div className="bg-white border border-stone-200 p-4">
        <label className={L}>{t("exps.notes")}</label>
        <textarea data-testid="exp-notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="w-full px-3 py-2 border border-stone-200 text-[15px]" />
      </div>

      {error && <div data-testid="exp-error" className="text-sm text-red-700 bg-red-50 border border-red-200 p-3">{error}</div>}
      <button data-testid="exp-submit-btn" disabled={saving || scanning} className="w-full h-14 bg-[#1F3D34] text-white font-bold uppercase tracking-wide disabled:opacity-50">
        {saving ? t("exps.submitting") : t("exps.submit")}
      </button>
    </form>
  );
}
